import { useCallback, useEffect, useState } from "react";


import { WORDPRESS_API } from "shared/constants/api";


const WORDPRESS_API_LINK = `${WORDPRESS_API}/wp-json/wp/v2`;


export const useNewsData = ({
  page = 1,
  perPage = 8
} = {}) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);


  const loadNews = useCallback(async (signal) => {
    try {
      setLoading(true);
      setError(null);

      //
      // 1. Pobranie kategorii
      //
      const categoriesResponse = await fetch(
        `${WORDPRESS_API_LINK}/categories?per_page=100`,
        { signal }
      );

      if (!categoriesResponse.ok) {
        throw new Error(
          "Nie udało się pobrać kategorii."
        );
      }

      const categories =
        await categoriesResponse.json();

      const categoryMap = {};

      categories.forEach((category) => {
        categoryMap[category.id] = category.name;
      });

      const newsCategory = categories.find(
        (category) =>
          category.name?.toLowerCase() ===
          "aktualności"
      );

      if (!newsCategory) {
        throw new Error(
          'Nie znaleziono kategorii "Aktualności"'
        );
      }

      //
      // 2. Pobranie postów z wybranej strony
      //
      const postsResponse = await fetch(
        `${WORDPRESS_API_LINK}/posts?categories=${newsCategory.id}&per_page=${perPage}&page=${page}`,
        { signal }
      );

      if (!postsResponse.ok) {
        throw new Error(
          "Nie udało się pobrać aktualności."
        );
      }

      //
      // WordPress zwraca informacje o paginacji
      // w nagłówkach odpowiedzi.
      //
      const postsTotal = Number(
        postsResponse.headers.get("X-WP-Total") || 0
      );

      const pagesTotal = Number(
        postsResponse.headers.get("X-WP-TotalPages") || 0
      );

      const posts = await postsResponse.json();


      //
      // 3. Mapowanie postów
      //
      const mapped = posts.map((post) => {
        const html =
          post.content?.rendered || "";

        const parser = new DOMParser();

        const doc = parser.parseFromString(
          html,
          "text/html"
        );

        //
        // wszystkie zdjęcia z treści
        //
        const images = Array.from(
          doc.querySelectorAll("img")
        )
          .map((img) => img.src)
          .filter(Boolean);

        //
        // losowe zdjęcie
        //
        let randomImage = "";

        if (images.length > 0) {
          randomImage =
            images[
              Math.floor(
                Math.random() * images.length
              )
            ];
        }

        const excerpt =
          doc.body.textContent || "";

        const categoryName =
          post.categories?.length > 0
            ? categoryMap[post.categories[0]]
            : "Bez kategorii";

        return {
          id: post.id,
          title: post.title.rendered,
          link: post.link,
          date: post.date,
          category: categoryName,
          image: randomImage,
          excerpt:
            excerpt.length > 180
              ? excerpt.substring(0, 180) + "..."
              : excerpt
        };
      });


      if (!signal?.aborted) {
        setNews(mapped);
        setTotal(postsTotal);
        setTotalPages(pagesTotal);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error(err);

        setError(err);
        setNews([]);
        setTotal(0);
        setTotalPages(0);
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [page, perPage]);


  useEffect(() => {
    const controller = new AbortController();

    loadNews(controller.signal);

    return () => {
      controller.abort();
    };
  }, [loadNews]);


  const reload = () => {
    loadNews();
  };


  return {
    news,
    loading,
    error,
    total,
    totalPages,
    reload
  };
};